<template>
  <Transition name="cart-reminder">
    <div v-if="isVisible" class="cart-reminder" role="status">
      <p class="cart-reminder__message">
        You have {{ lineItemsCount }}
        {{ lineItemsCount === 1 ? "item" : "items" }} left in your bag.
        <button
          type="button"
          class="cart-reminder__link"
          @click="handleViewCart"
        >
          View cart
        </button>
      </p>
    </div>
  </Transition>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import {
  hasDismissedCartReminder,
  hasJustAddedOnCurrentPage,
  hasVisitedCartThisSession,
  markCartReminderDismissed,
} from "@/helpers/cartReminder.js";

const ANIMATION_MS = 300;
const MAX_LOAD_DELAY_MS = 2000;

export default {
  name: "CartReminderBanner",
  data() {
    return {
      isVisible: false,
      hasTriggered: false,
      loadTimerId: null,
      holdTimerId: null,
      dismissTimerId: null,
    };
  },
  computed: {
    ...mapState("cart", ["isCartOpen", "cart"]),
    ...mapGetters("cart", ["lineItemsCount"]),
    displayDurationMs() {
      const { cartReminder5s = false, cartReminderDuration = 10 } =
        getFeatureToggles();
      const seconds = cartReminder5s ? 5 : Number(cartReminderDuration);

      if (!Number.isFinite(seconds) || seconds <= 0) {
        return 10000;
      }

      return seconds * 1000;
    },
    isEligible() {
      if (!this.cart) return false;
      if (this.lineItemsCount < 1) return false;
      if (hasDismissedCartReminder()) return false;
      if (hasVisitedCartThisSession()) return false;
      if (hasJustAddedOnCurrentPage()) return false;
      if (this.isCartOpen) return false;
      return true;
    },
  },
  watch: {
    isEligible: {
      immediate: true,
      handler(eligible) {
        if (eligible) {
          this.scheduleTriggers();
          return;
        }

        this.clearLoadTimer();
        if (this.isVisible) {
          this.dismiss({ persist: false });
        }
      },
    },
    lineItemsCount(count, previousCount) {
      // Cart often loads after mount — re-check once items appear
      if (count > 0 && previousCount === 0) {
        this.hasTriggered = false;
        this.scheduleTriggers();
      }
    },
    isCartOpen(isOpen) {
      if (isOpen && this.isVisible) {
        this.dismiss({ persist: true });
      }
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    // Cart fetch is async; retry shortly after mount in case eligibility
    // flipped before the watcher ran with a stable cart.
    this.scheduleTriggers();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    this.clearTimers();
  },
  methods: {
    ...mapActions("cart", ["setCartOpen"]),
    scheduleTriggers() {
      if (!this.isEligible || this.hasTriggered || this.loadTimerId) return;

      const delay = Math.random() * MAX_LOAD_DELAY_MS;
      this.loadTimerId = window.setTimeout(() => {
        this.loadTimerId = null;
        this.trigger();
      }, delay);
    },
    handleScroll() {
      if (this.hasTriggered || !this.isEligible) return;
      if (!this.hasScrolledPastAddToBag()) return;

      this.clearLoadTimer();
      this.trigger();
    },
    hasScrolledPastAddToBag() {
      const addToBag = document.querySelector('[data-testid="add-to-bag"]');
      if (!addToBag) return false;

      return addToBag.getBoundingClientRect().bottom < 0;
    },
    trigger() {
      if (this.hasTriggered || !this.isEligible) return;

      this.hasTriggered = true;
      this.isVisible = true;

      this.holdTimerId = window.setTimeout(() => {
        this.holdTimerId = null;
        this.dismiss({ persist: true });
      }, this.displayDurationMs);
    },
    dismiss({ persist = false } = {}) {
      this.clearLoadTimer();
      this.clearHoldTimer();

      if (!this.isVisible) {
        if (persist) {
          markCartReminderDismissed();
        }
        return;
      }

      this.isVisible = false;

      if (persist) {
        markCartReminderDismissed();
      }

      this.dismissTimerId = window.setTimeout(() => {
        this.dismissTimerId = null;
      }, ANIMATION_MS);
    },
    handleViewCart() {
      markCartReminderDismissed();
      this.dismiss({ persist: true });
      this.setCartOpen(true);
    },
    clearLoadTimer() {
      if (this.loadTimerId) {
        clearTimeout(this.loadTimerId);
        this.loadTimerId = null;
      }
    },
    clearHoldTimer() {
      if (this.holdTimerId) {
        clearTimeout(this.holdTimerId);
        this.holdTimerId = null;
      }
    },
    clearTimers() {
      this.clearLoadTimer();
      this.clearHoldTimer();
      if (this.dismissTimerId) {
        clearTimeout(this.dismissTimerId);
        this.dismissTimerId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-reminder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  padding: 0 12px;
  text-align: center;
}

.cart-reminder__message {
  margin: 0;
  font-family: $utility-font-family;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: 0.65px;
  color: $color-black;
}

.cart-reminder__link {
  font: inherit;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.85;
  }
}

.cart-reminder-enter-active {
  transition: transform 300ms ease-in;
}

.cart-reminder-leave-active {
  transition: transform 300ms ease-out;
}

.cart-reminder-enter-from,
.cart-reminder-leave-to {
  transform: translateY(-100%);
}

.cart-reminder-enter-to,
.cart-reminder-leave-from {
  transform: translateY(0);
}
</style>
